import { useDashboardData } from '@/hooks/useSyncedData';
import KPICard from './KPICard';
import PortfolioChart from './PortfolioChart';
import BudgetChart from './BudgetChart';
import ProjectList from './ProjectList';
import VelocityChart from './VelocityChart';
import { SignalWidget } from '@/components/signals/SignalWidget';
import SyncControlPanel from './SyncControlPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Database } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocalization } from '@/hooks/useLocalization';

interface DashboardProps {
  onNavigateToSignals?: () => void;
}

const Dashboard = ({ onNavigateToSignals }: DashboardProps) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const {
    kpis,
    projects,
    hasSyncedData,
    isLoading,
    syncedProjectsCount,
    syncedKPIsCount,
    syncedTasksCount,
    portfolioHealthData,
    budgetSeries,
    velocitySeries,
  } = useDashboardData();

  const handleRefresh = async () => {
    toast.info(t('Refreshing dashboard data...'));
    await queryClient.invalidateQueries({ queryKey: ['synced-projects'] });
    await queryClient.invalidateQueries({ queryKey: ['synced-kpis'] });
    await queryClient.invalidateQueries({ queryKey: ['synced-tasks'] });
    toast.success(t('Dashboard refreshed'));
  };

  const totalSyncedRecords = syncedProjectsCount + syncedKPIsCount + syncedTasksCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="masira-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-foreground">{t('Portfolio overview')}</h2>
            {hasSyncedData && (
              <Badge variant="secondary" className="gap-1">
                <Database className="h-3 w-3" />
                
                {t('Live Data')}
              </Badge>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 me-2 ${isLoading ? 'animate-spin' : ''}`} />
            
            {t('Refresh')}
          </Button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {hasSyncedData
            ? t('{0} projects · {1} tasks · {2} KPIs · {3} synced records', { 0: syncedProjectsCount, 1: syncedTasksCount, 2: syncedKPIsCount, 3: totalSyncedRecords })
            : t('Real-time overview of your project portfolio')}
        </p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <KPICard key={kpi.id} kpi={kpi} index={index} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PortfolioChart data={portfolioHealthData} />
        <BudgetChart data={budgetSeries} />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ProjectList projects={projects} />
        <VelocityChart data={velocitySeries} />
        <SignalWidget onViewAll={onNavigateToSignals} />
      </div>

      {/* Sync controls: operational detail, kept compact and out of the way */}
      <SyncControlPanel />
    </div>
  );
};

export default Dashboard;
